'use client';

import { useEffect, useRef } from 'react';

export function PersianNumberConverter() {
  const toPersianNumber = (num: number | string): string => {
    const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
    return num.toString().replace(/\d/g, (d) => persianDigits[parseInt(d)]);
  };

  const toEnglishNumber = (num: string): string => {
    const persianDigits = '۰۱۲۳۴۵۶۷۸۹';
    const englishDigits = '0123456789';
    return num.replace(/[۰-۹]/g, (d) => englishDigits[persianDigits.indexOf(d)]);
  };

  useEffect(() => {
    // تابع تبدیل مقادیر input با حفظ cursor
    const convertInputValue = (input: HTMLInputElement | HTMLTextAreaElement) => {
      if (input.value && /\d/.test(input.value)) {
        const cursorPosition = input.selectionStart;
        const converted = input.value.replace(/\d+/g, (match) => toPersianNumber(match));
        if (input.value !== converted) {
          input.value = converted;
          if (cursorPosition !== null) {
            input.setSelectionRange(cursorPosition, cursorPosition);
          }
          // dispatch رویداد برای React
          const event = new Event('input', { bubbles: true });
          input.dispatchEvent(event);
        }
      }
    };

    // intercept کردن event handlerهای React
    const originalInputSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
    const originalTextareaSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value');

    // پچ کردن setter value برای input
    if (originalInputSetter) {
      Object.defineProperty(HTMLInputElement.prototype, 'value', {
        get: originalInputSetter.get,
        set: function(value) {
          const stringValue = String(value);
          // اگر مقدار جدید عدد دارد، به فارسی تبدیل کن
          const persianValue = stringValue.replace(/\d+/g, (match) => toPersianNumber(match));
          originalInputSetter.set?.call(this, persianValue);
        }
      });
    }

    // تبدیل اعداد در text nodes
    const convertTextNodes = (element: Element | Document = document) => {
      const walker = document.createTreeWalker(
        element,
        NodeFilter.SHOW_TEXT,
        {
          acceptNode: (node) => {
            const parent = node.parentElement;
            if (parent?.tagName === 'SCRIPT' || 
                parent?.tagName === 'STYLE' || 
                parent?.tagName === 'CODE' ||
                parent?.closest('[data-no-convert]')) {
              return NodeFilter.FILTER_REJECT;
            }
            return NodeFilter.FILTER_ACCEPT;
          }
        }
      );

      const textNodes: Text[] = [];
      let node: Text | null;
      
      while (node = walker.nextNode() as Text | null) {
        if (node.textContent && /\d/.test(node.textContent)) {
          textNodes.push(node);
        }
      }

      textNodes.forEach((node) => {
        if (node.textContent) {
          const converted = node.textContent.replace(/\d+/g, (match) => toPersianNumber(match));
          if (node.textContent !== converted) {
            node.textContent = converted;
          }
        }
      });
    };

    // تبدیل اعداد در stateهای React با استفاده از MutationObserver پیشرفته
    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      
      mutations.forEach((mutation) => {
        if (mutation.type === 'characterData' && mutation.target.textContent) {
          needsUpdate = true;
        }
        if (mutation.type === 'childList' && mutation.addedNodes.length) {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              convertTextNodes(node as Element);
            }
          });
        }
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'value' || mutation.attributeName === 'textContent')) {
          needsUpdate = true;
        }
      });
      
      if (needsUpdate) {
        convertTextNodes(document.body);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      characterDataOldValue: true,
      attributes: true,
      attributeFilter: ['value', 'textContent']
    });

    // تبدیل اولیه
    convertTextNodes(document.body);
    
    // تبدیل مقادیر inputهای موجود
    document.querySelectorAll('input, textarea').forEach((input) => {
      if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
        convertInputValue(input);
      }
    });

    return () => {
      observer.disconnect();
      // بازگرداندن setter اصلی
      if (originalInputSetter) {
        Object.defineProperty(HTMLInputElement.prototype, 'value', originalInputSetter);
      }
      if (originalTextareaSetter) {
        Object.defineProperty(HTMLTextAreaElement.prototype, 'value', originalTextareaSetter);
      }
    };
  }, []);

  return null;
}